import cn from 'classnames';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { ScreenWidth } from 'web-common/src/shared/styles';
import { LayoutContainer } from './LayoutContainer';

const COPY = {
  CTA: 'Explore Weekly Shef Service',
  heading: 'A DINNER TIME SHORTCUT YOU CAN FEEL GOOD ABOUT',
  subHeading:
    // eslint-disable-next-line max-len -- backoff eslint
    'With the Weekly Shef Service, your shef cooks for you and a small group of families each week — meals are made fresh on your delivery day, carefully packed, and delivered to your door.',
};

const DEFAULT_SCALE = {
  [ScreenWidth.EXTRA_EXTRA_SMALL]: 0.6,
  [ScreenWidth.EXTRA_SMALL]: 0.7,
  [ScreenWidth.SMALL]: 0.8,
  [ScreenWidth.MEDIUM]: 0.9,
  [ScreenWidth.LARGE]: 1.0,
  [ScreenWidth.EXTRA_LARGE]: 1.0,
};

const DEFAULT_MARGIN = {
  [ScreenWidth.EXTRA_EXTRA_SMALL]: -3,
  [ScreenWidth.EXTRA_SMALL]: -2.5,
  [ScreenWidth.SMALL]: -2,
  [ScreenWidth.MEDIUM]: -1.5,
  [ScreenWidth.LARGE]: -0.8,
  [ScreenWidth.EXTRA_LARGE]: 1,
};

interface FloatingImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  image: StaticImage;
}

const FloatingImage = ({ image, className }: FloatingImageProps) => {
  const { nearestScreenWidth } = useScreenContext();
  const scaleDim = (dim: number) => Math.floor(dim * DEFAULT_SCALE[nearestScreenWidth]);
  const marginLeft = image.marginLeft ? image.marginLeft + DEFAULT_MARGIN[nearestScreenWidth] : 0;
  const marginRight = image.marginRight ? image.marginRight + DEFAULT_MARGIN[nearestScreenWidth] : 0;
  const marginBottom = image.marginBottom ? image.marginBottom : 0;
  const marginTop = image.marginTop ? image.marginTop : 0;

  return (
    <img
      alt={image.alt}
      style={{
        marginLeft: marginLeft ? `${marginLeft}rem` : 0,
        marginRight: marginRight ? `${marginRight}rem` : 0,
        marginBottom: marginBottom ? `${marginBottom}rem` : 0,
        marginTop: marginTop ? `${marginTop}rem` : 0,
        transform: `rotate(${image.rotationDeg}deg)`,
      }}
      src={generateThumborImgUrl({ src: image.url, width: scaleDim(image.width), height: scaleDim(image.height) })}
      width={`${scaleDim(image.width)}px`}
      height={`${scaleDim(image.height)}px`}
      className={cn('shrink-0 grow-0 rounded-xl', className)}
    />
  );
};

const IMG_URL_TL = 'https://shef-general.s3.us-west-1.amazonaws.com/0e94ff31216f0886f1f873f8e7437d4d.jpeg';
const IMG_URL_BR = 'https://shef-general.s3.us-west-1.amazonaws.com/631192b56e900834bdc92f71bff5b676.jpeg';
const IMG_URL_TR = 'https://shef-general.s3.us-west-1.amazonaws.com/b8e20648a5c2b58f74ad285cd0a329f8.jpeg';
const IMG_URL_BL = 'https://shef-general.s3.us-west-1.amazonaws.com/cc4316c0f7e84ce7b053d103fbda76af.jpeg';

type StaticImage = {
  url: string;
  rotationDeg: number;
  marginLeft?: number;
  marginRight?: number;
  marginBottom?: number;
  marginTop?: number;
  width: number;
  height: number;
  alt: string;
};

type ImagePlacement = {
  top: { left: StaticImage; right: StaticImage };
  bottom: { left: StaticImage; right: StaticImage };
};

const IMGS_SMALL: ImagePlacement = {
  top: {
    right: {
      url: IMG_URL_TR,
      rotationDeg: 8.89,
      marginRight: -0.5,
      width: 332,
      height: 470,
      alt: [
        'a chef preparing a sauce for a meal, visible on a plate of grilled steak over a red ',
        'sauce, salad, garbanzo beans ',
      ].join(),
    },
    left: {
      url: IMG_URL_TL,
      rotationDeg: -7.139,
      marginLeft: -7,
      width: 314,
      height: 368,
      alt: 'a fresh salad of lettuce, arugula, tomatoes, chopped onions, and cucumbers atop a raspberry tablecloth',
    },
  },
  bottom: {
    right: {
      url: IMG_URL_BR,
      rotationDeg: 8.906,
      marginRight: -8,
      width: 334,
      height: 412,
      alt: 'a mixed bowl of fresh salad, nuts, rice, and pita bread slices on a table',
    },
    left: {
      url: IMG_URL_BL,
      rotationDeg: -5.065,
      marginLeft: -1,
      width: 420,
      height: 470,
      alt: 'a person serving meatballs in a marinara sauce from a stainless steal saucepan',
    },
  },
};

const IMGS_LARGE: ImagePlacement = {
  top: {
    right: {
      url: IMG_URL_BR,
      rotationDeg: 8.89,
      marginLeft: -2,
      marginBottom: -2,
      width: 332,
      height: 470,
      alt: [
        'a chef preparing a sauce for a meal, visible on a plate of grilled steak over a red ',
        'sauce, salad, garbanzo beans ',
      ].join(),
    },
    left: {
      url: IMG_URL_TL,
      rotationDeg: -7.139,
      marginLeft: -5,
      marginBottom: -4,
      width: 314,
      height: 368,
      alt: 'a fresh salad of lettuce, arugula, tomatoes, chopped onions, and cucumbers atop a raspberry tablecloth',
    },
  },
  bottom: {
    right: {
      url: IMG_URL_TR,
      rotationDeg: 8.906,
      marginLeft: 0,
      width: 334,
      height: 412,
      alt: 'a mixed bowl of fresh salad, nuts, rice, and pita bread slices on a table',
    },
    left: {
      url: IMG_URL_BL,
      rotationDeg: -5.065,
      marginRight: -0.5,
      marginTop: -1,
      width: 420,
      height: 470,
      alt: 'a person serving meatballs in a marinara sauce from a stainless steal saucepan',
    },
  },
};

type TopValueCalloutSectionProps = {
  mealPlanButtonOnClick: () => void;
};

const SectionCopy = () => (
  <>
    <h1 className='mb-8 text-5xl font-black uppercase text-eggplant'>{COPY.heading}</h1>
    <p className='text-xl font-normal text-eggplant'>{COPY.subHeading}</p>
  </>
);

interface CTAButtonProps extends TopValueCalloutSectionProps {
  className?: string;
}

const CTAButton = ({ mealPlanButtonOnClick, className }: CTAButtonProps) => (
  <div className={className}>
    <Button onClick={mealPlanButtonOnClick} buttonType={ButtonType.Primary} iconName='arrowRight' iconPosition='right'>
      {COPY.CTA}
    </Button>
  </div>
);

const SmallLayout = ({ mealPlanButtonOnClick }: TopValueCalloutSectionProps) => (
  <div className='xl:hidden flex flex-col overflow-hidden lg:hidden'>
    <div
      className='mt-10 flex basis-1 flex-row justify-between'
      style={{ backgroundImage: 'linear-gradient(to bottom, white 43%, #FFDBC7 43%)' }}>
      <FloatingImage image={IMGS_SMALL.top.left} />
      <FloatingImage image={IMGS_SMALL.top.right} />
    </div>

    <div className='basis-1  bg-sourdough py-10'>
      <LayoutContainer>
        <SectionCopy />
      </LayoutContainer>
    </div>

    <div
      className='flex basis-3 flex-row justify-between'
      style={{ backgroundImage: 'linear-gradient(to bottom, #FFDBC7 80%, #FFF9F2 80%)' }}>
      <div className='mb-10 mr-4 flex h-full flex-col gap-10'>
        <LayoutContainer noMarginRight>
          <CTAButton mealPlanButtonOnClick={mealPlanButtonOnClick} />
        </LayoutContainer>
        <FloatingImage image={IMGS_SMALL.bottom.left} />
      </div>
      <FloatingImage image={IMGS_SMALL.bottom.right} className='self-start' />
    </div>
  </div>
);

const LargeLayout = ({ mealPlanButtonOnClick }: TopValueCalloutSectionProps) => (
  <div className='bg-sourdough py-20 max-lg:hidden'>
    <LayoutContainer className=''>
      <section className='flex flex-row gap-x-20'>
        <div>
          <SectionCopy />
          <CTAButton mealPlanButtonOnClick={mealPlanButtonOnClick} className='my-8 w-auto' />
        </div>
        <div className='flex-row'>
          <div className='flex flex-col'>
            <FloatingImage image={IMGS_LARGE.top.left} />
            <FloatingImage image={IMGS_LARGE.bottom.left} className='z-10' />
          </div>
          <div className='flex flex-col'>
            <FloatingImage image={IMGS_LARGE.top.right} />
            <FloatingImage image={IMGS_LARGE.bottom.right} />
          </div>
        </div>
      </section>
    </LayoutContainer>
  </div>
);

export const TopValueCalloutSection = ({ mealPlanButtonOnClick }: TopValueCalloutSectionProps) => (
  <>
    <LargeLayout mealPlanButtonOnClick={mealPlanButtonOnClick} />
    <SmallLayout mealPlanButtonOnClick={mealPlanButtonOnClick} />
  </>
);
